import { Component } from '@theme/component';

/**
 * Albertina configurator.
 *
 * The stage stacks full-frame layers that all share one photo framing:
 *   vase (colour × finish) → scale figure → shade (fabric) → fringe.
 * Every option pre-renders its <img>; changing a choice only toggles `is-active`,
 * so hovering an option is an instant swap — no layout, no fetch on click.
 *
 * State lives in `selected`; `preview` holds the hovered override and is merged on render.
 */
class AlbertinaConfigurator extends Component {
  requiredRefs = ['vaseLayer', 'shadeLayer'];

  /** @type {Record<string, string | boolean>} */
  selected = {
    environment: 'indoor',
    colour: '',
    finish: 'matt',
    fabric: '',
    fringe: 'none',
  };

  /** @type {Partial<typeof this.selected> | null} */
  preview = null;

  /** @type {Record<string, number>} */
  #tokens = {};

  /** Current reference code, e.g. ALB-V04-G-CE001 (kept for the PDF sheet). */
  #reference = '';

  connectedCallback() {
    super.connectedCallback();

    this.selected.colour = this.dataset.defaultColour || '';
    this.selected.fabric = this.dataset.defaultFabric || '';
    this.#readUrl();
    // Shopify's contact form redirects back without our ?config: restore the configuration that was sent
    if (this.dataset.openQuote === 'success' && !new URLSearchParams(window.location.search).get('config')) {
      try {
        const saved = sessionStorage.getItem('albertina-config');
        if (saved) this.#applyConfig(saved);
      } catch {}
    }
    this.querySelector('form.configurator__form')?.addEventListener('submit', (event) => {
      try { sessionStorage.setItem('albertina-config', this.#configString()); } catch {}
      // Collenti's notification mail shows the message first: lead with the reference and the configuration
      const body = event.target instanceof HTMLFormElement ? event.target.querySelector('textarea[name="contact[body]"]') : null;
      if (body instanceof HTMLTextAreaElement) {
        const label = this.dataset.labelReference ?? 'Reference';
        const line = this.querySelector('[ref="summaryLine"]')?.textContent ?? '';
        const header = `${label}: ${this.#reference}\n${line}\n${window.location.href}`;
        body.value = body.value.trim() ? `${header}\n\n${body.value.trim()}` : header;
      }
    });
    this.#ensureFabricForEnvironment();

    // Refs are populated synchronously by Component, so render right away (rAF would stall in a background tab).
    this.render();
    if (this.dataset.openQuote) this.#quoteDialog()?.showDialog();
    if (this.dataset.openQuote === 'success') this.afterSubmitSuccess();
  }

  /**
   * Reference recall: "ALB-V05-G-LN02-FR02" -> selection. Segments are matched on the option codes (colour V.., fabric,
   * fringe) and the finish letter; the prefix is optional; order does not matter. Environment follows the fabric.
   * @param {Event} event
   */
  applyReference(event) {
    event.preventDefault();
    const { recallInput, recallStatus } = this.refs;
    if (!(recallInput instanceof HTMLInputElement)) return;
    const raw = recallInput.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    const parts = raw.split('-').filter(Boolean).filter((p) => p !== (this.dataset.codePrefix || 'ALB').toUpperCase());
    const byCode = (step, code) => [...this.querySelectorAll(`[data-step="${step}"]`)].find((el) => el instanceof HTMLElement && (el.dataset.code || '').toUpperCase() === code);
    const next = { ...this.selected };
    let found = 0;
    for (const part of parts) {
      let el;
      if ((el = byCode('colour', part))) { next.colour = el.dataset.value ?? next.colour; found++; continue; }
      if ((el = byCode('fabric', part))) { next.fabric = el.dataset.value ?? next.fabric; next.environment = el.dataset.environment === 'outdoor' ? 'outdoor' : 'indoor'; found++; continue; }
      if ((el = byCode('fringe', part))) { next.fringe = el.dataset.value ?? next.fringe; found++; continue; }
      if (part === 'M' || part === 'G') { next.finish = part === 'M' ? 'matt' : 'glossy'; found++; continue; }
    }
    const ok = found > 0 && found === parts.length;
    if (recallStatus) {
      recallStatus.textContent = ok ? this.dataset.labelRecallOk ?? '' : this.dataset.labelRecallError ?? '';
      recallStatus.classList.toggle('is-error', !ok);
    }
    if (!ok) return;
    this.preview = null;
    this.selected = next;
    if (next.environment === 'outdoor') this.selected.fringe = 'none';
    this.#ensureFabricForEnvironment();
    this.render();
    this.#writeUrl();
    recallInput.value = this.#reference;
  }

  recallTyping() {
    const { recallStatus } = this.refs;
    if (recallStatus) { recallStatus.textContent = ''; recallStatus.classList.remove('is-error'); }
  }

  /** The configuration was sent to Collenti: offer the PDF right away (the confirmation also has a button). */
  afterSubmitSuccess() {
    this.#writeUrl();
    setTimeout(() => this.savePdf(), 900);
  }

  /** Re-applies the state after the Section Rendering API (theme editor) swaps our markup. */
  updatedCallback() {
    super.updatedCallback();
    this.#ensureFabricForEnvironment();
    this.render();
  }

  /* ------------------------------------------------------------------ events */

  /** @param {Event} event */
  select(event) {
    const { step, value } = this.#option(event);
    if (!step) return;

    this.preview = null;
    this.selected[step] = value;
    if (step === 'environment') {
      this.#ensureFabricForEnvironment();
      if (value === 'outdoor') this.selected.fringe = 'none'; // fringes are indoor only
    }

    this.render();
    this.#writeUrl();
  }

  /** @param {Event} event */
  hoverOption(event) {
    if (window.matchMedia('(hover: none)').matches) return;
    const { step, value } = this.#option(event);
    if (!step || this.selected[step] === value) return;

    this.preview = { [step]: value };
    this.render({ previewOnly: true });
  }

  leaveOption() {
    if (!this.preview) return;
    this.preview = null;
    this.render({ previewOnly: true });
  }

  /**
   * "Save as PDF for myself": composes the active layers onto a canvas exactly as the stage shows them, builds a
   * print-only sheet (image + configuration + reference) at the end of <body>, hides everything else while
   * printing and opens the browser's print dialog, where the visitor picks "Save as PDF". No server involved.
   */
  async savePdf() {
    document.querySelector('.configurator-print')?.remove();
    const image = await this.#composite();
    const sheet = document.createElement('div');
    sheet.className = 'configurator-print';
    sheet.setAttribute('aria-hidden', 'true');

    const rows = ['environment', 'colour', 'finish', 'fabric', 'fringe']
      .map((step) => {
        const value = String(this.selected[step]);
        if (step === 'fringe' && value === 'none') return null;
        const option = this.#optionElement(step, value);
        const title = this.querySelector(`[data-current="${step}"]`)?.closest('.configurator__step')?.querySelector('.configurator__step-title')?.textContent?.trim();
        const label = this.#label(step, value);
        if (!option || !title || !label) return null;
        const code = option.dataset.code;
        // thumbnail = the option's own tile image (vase in the chosen finish, full lampshade, fringe)
        let thumb = null;
        if (step === 'colour' || step === 'finish') {
          const colour = this.#optionElement('colour', String(this.selected.colour));
          thumb = colour?.querySelector(`img[data-finish="${this.selected.finish}"]`) ?? colour?.querySelector('img');
        } else {
          thumb = option.querySelector('img');
        }
        const src = thumb instanceof HTMLImageElement ? thumb.currentSrc || thumb.getAttribute('src') || thumb.dataset.src : null;
        const media = src ? `<img class="configurator-print__thumb" src="${src}" alt="">` : '<span class="configurator-print__thumb configurator-print__thumb--empty"></span>';
        return `<div class="configurator-print__row">${media}<div><dt>${title}</dt><dd>${label}${code ? ` <span class="configurator-print__code">${code}</span>` : ''}</dd></div></div>`;
      })
      .filter(Boolean)
      .join('');
    const url = new URL(window.location.href);
    url.searchParams.set('config', [this.selected.environment, this.selected.colour, this.selected.finish, this.selected.fabric, this.selected.fringe].join('.'));
    const date = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { dateStyle: 'long' }).format(new Date());
    const brand = (this.dataset.printFooter ?? '').split('·')[0].trim();

    sheet.innerHTML = `
      <div class="configurator-print__head">
        <p class="configurator-print__brand">${brand}</p>
        <p class="configurator-print__eyebrow">${this.dataset.labelReference ?? ''} ${this.#reference}</p>
      </div>
      <div class="configurator-print__body">
        <img class="configurator-print__image" alt="">
        <div>
          <h1 class="configurator-print__title">${this.dataset.labelTitle ?? ''}</h1>
          <div class="configurator-print__rows">${rows}</div>
          <p class="configurator-print__ref"><small>${this.dataset.labelReference ?? ''}</small>${this.#reference}</p>
          <p class="configurator-print__meta">${this.dataset.labelDate ?? ''}: ${date}<br>${this.dataset.labelLink ?? ''}:<br><a href="${url.href}">${url.href}</a></p>
        </div>
      </div>
      <p class="configurator-print__footer"><span>${this.dataset.printFooter ?? ''}</span><span>${this.#reference}</span></p>`;
    const img = sheet.querySelector('.configurator-print__image');
    if (img instanceof HTMLImageElement) img.src = image;
    document.body.append(sheet);
    await Promise.all([...sheet.querySelectorAll('img')].map((el) => (el instanceof HTMLImageElement && !el.complete ? new Promise((r) => { el.onload = el.onerror = r; }) : null)));

    const root = document.documentElement;
    root.classList.add('configurator-printing');
    const done = () => {
      root.classList.remove('configurator-printing');
      sheet.remove();
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    window.print();
  }

  /**
   * Draws the current scene (active layer images, frame shift, floor strip)
   * onto a 1200x1800 canvas and returns it as a data URL. Falls back to the vase image alone if the canvas is
   * tainted by a cross-origin image without CORS headers.
   */
  async #composite() {
    const W = 1200, H = 1800;
    const shift = Math.round(H * (parseFloat(getComputedStyle(this).getPropertyValue('--configurator-frame-shift')) || 7.5) / 100);
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    const load = (src) => new Promise((resolve) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => resolve(im);
      im.onerror = () => resolve(null);
      im.src = src;
    });
    const active = (selector) => {
      const el = this.querySelector(`${selector} img.is-active`) ?? this.querySelector(`${selector} img`);
      return el?.currentSrc || el?.getAttribute('src') || null;
    };
    const layers = [active('.configurator__layer--vase'), active('.configurator__layer--figure'), active('.configurator__layer--shade')];
    layers.push(active('.configurator__layer--fringe'));
    const floor = this.querySelector('.configurator__floor img')?.getAttribute('src');
    const [floorImg, ...imgs] = await Promise.all([floor ? load(floor) : null, ...layers.map((src) => (src ? load(src) : null))]);
    ctx.fillStyle = '#e6d5b3';
    ctx.fillRect(0, 0, W, H);
    for (const im of imgs) if (im) ctx.drawImage(im, 0, -shift, W, H);
    if (floorImg) {
      const overlap = Math.round(H * 0.01);
      ctx.drawImage(floorImg, 0, H - shift - overlap, W, shift + overlap);
    }
    try {
      return canvas.toDataURL('image/jpeg', 0.9);
    } catch {
      return layers[0] ?? '';
    }
  }

  /* ------------------------------------------------------------------ render */

  /** @param {{ previewOnly?: boolean }} [options] */
  render({ previewOnly = false } = {}) {
    const state = { ...this.selected, ...(this.preview ?? {}) };
    const { vaseLayer, shadeLayer, fringeLayer, swatchCard, swatchImage, swatchLabel } = this.refs;

    this.dataset.finish = String(state.finish);
    this.dataset.environment = String(state.environment);
    this.classList.toggle('is-previewing', Boolean(this.preview));

    this.#activate(vaseLayer, `${state.colour}:${state.finish}`);
    this.#activate(shadeLayer, String(state.fabric));
    if (fringeLayer) this.#activate(fringeLayer, String(state.fringe));

    // Flat fabric square next to the lamp (client request: show the plain cloth, not only the pleated shade).
    const fabricOption = this.#optionElement('fabric', String(state.fabric));
    if (swatchCard && swatchImage instanceof HTMLImageElement) {
      const swatch = fabricOption?.dataset.swatch;
      swatchCard.hidden = !swatch;
      if (swatch && swatchImage.getAttribute('src') !== swatch) swatchImage.src = swatch;
      if (swatchLabel) swatchLabel.textContent = fabricOption?.dataset.label ?? '';
    }

    if (previewOnly) return;

    for (const option of this.querySelectorAll('[data-step]')) {
      if (!(option instanceof HTMLElement)) continue;
      const active = this.selected[option.dataset.step ?? ''] === option.dataset.value;
      option.classList.toggle('is-selected', active);
      option.setAttribute('aria-pressed', String(active));
    }

    for (const current of this.querySelectorAll('[data-current]')) {
      if (!(current instanceof HTMLElement)) continue;
      const step = current.dataset.current ?? '';
      current.textContent = this.#label(step, String(this.selected[step]));
    }

    this.#renderSummary();
  }

  /**
   * Switches a layer to the <img data-key="key"> child. The new image is loaded before the
   * swap so a hover never flashes an empty frame; a token guards against out-of-order loads.
   * @param {HTMLElement | undefined} layer
   * @param {string} key
   */
  async #activate(layer, key) {
    if (!layer) return;
    const next = layer.querySelector(`img[data-key="${CSS.escape(key)}"]`);
    const token = (this.#tokens[layer.id] = (this.#tokens[layer.id] ?? 0) + 1);

    if (next instanceof HTMLImageElement) {
      if (!next.getAttribute('src')) next.src = next.dataset.src ?? '';
      if (!next.complete) {
        // Wait for the network, but never longer than the fade itself: a slow or broken image must
        // not leave the previous option stuck on screen. (img.decode() can stall in hidden tabs.)
        await new Promise((resolve) => {
          const done = () => resolve(undefined);
          next.addEventListener('load', done, { once: true });
          next.addEventListener('error', done, { once: true });
          setTimeout(done, 1200);
        });
      }
      if (this.#tokens[layer.id] !== token) return;
    }

    for (const img of layer.querySelectorAll('img')) {
      img.classList.toggle('is-active', img === next);
    }
    layer.classList.toggle('is-empty', !next);
  }

  #renderSummary() {
    const { summaryLine, summaryCode } = this.refs;
    // The quote form lives inside the nested <dialog-component>, so its refs belong to that
    // component — query them directly.
    const configField = this.querySelector('[ref="configField"]');
    const urlField = this.querySelector('[ref="urlField"]');
    const dialogSummary = this.querySelector('[ref="dialogSummary"]');
    const s = this.selected;

    const parts = [
      this.#label('environment', String(s.environment)),
      `${this.#label('colour', String(s.colour))} · ${this.#label('finish', String(s.finish))}`,
      this.#label('fabric', String(s.fabric)),
    ];
    if (s.fringe && s.fringe !== 'none') parts.push(this.#label('fringe', String(s.fringe)));

    const code = [
      this.dataset.codePrefix || null,
      this.#optionElement('colour', String(s.colour))?.dataset.code,
      String(s.finish).slice(0, 1).toUpperCase(),
      this.#optionElement('fabric', String(s.fabric))?.dataset.code,
      s.fringe !== 'none' ? this.#optionElement('fringe', String(s.fringe))?.dataset.code : null,
    ]
      .filter(Boolean)
      .join('-');

    const line = parts.filter(Boolean).join(' — ');
    if (summaryLine) summaryLine.textContent = line;
    if (dialogSummary) dialogSummary.textContent = line;
    if (summaryCode) summaryCode.textContent = code;
    if (configField instanceof HTMLInputElement) configField.value = `${line}${code ? ` (${code})` : ''}`;
    const referenceField = this.querySelector('[ref="referenceField"]');
    if (referenceField instanceof HTMLInputElement) referenceField.value = code;
    this.#reference = code;
    if (urlField instanceof HTMLInputElement) urlField.value = window.location.href;
  }

  /* --------------------------------------------------------------- helpers */

  /** Outdoor and indoor use different shades; keep the fabric choice valid for the environment. */
  #ensureFabricForEnvironment() {
    const current = this.#optionElement('fabric', String(this.selected.fabric));
    if (current && !current.hidden && this.#fits(current)) return;

    const first = [...this.querySelectorAll('[data-step="fabric"]')].find(
      (el) => el instanceof HTMLElement && this.#fits(el)
    );
    this.selected.fabric = first instanceof HTMLElement ? first.dataset.value ?? '' : '';
  }

  /** @param {HTMLElement} option */
  #fits(option) {
    const env = option.dataset.environment ?? 'both';
    return env === 'both' || env === this.selected.environment;
  }

  /** @param {Event} event */
  #option(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-step]') : null;
    return target instanceof HTMLElement ? { step: target.dataset.step ?? '', value: target.dataset.value ?? '' } : {};
  }

  /**
   * @param {string} step
   * @param {string} value
   */
  #optionElement(step, value) {
    const el = this.querySelector(`[data-step="${CSS.escape(step)}"][data-value="${CSS.escape(value)}"]`);
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * @param {string} step
   * @param {string} value
   */
  #label(step, value) {
    return this.#optionElement(step, value)?.dataset.label ?? '';
  }

  #quoteDialog() {
    const dialog = this.querySelector('dialog-component');
    return dialog && 'showDialog' in dialog ? /** @type {{ showDialog: () => void }} */ (dialog) : null;
  }

  /* ------------------------------------------------------------------- URL */

  #configString() {
    const s = this.selected;
    return [s.environment, s.colour, s.finish, s.fabric, s.fringe].join('.');
  }

  /** @param {string} raw  e.g. "indoor.bordeaux.glossy.celeste-001.none" */
  #applyConfig(raw) {
    const [environment, colour, finish, fabric, fringe] = raw.split('.');
    if (environment && this.#optionElement('environment', environment)) this.selected.environment = environment;
    if (colour && this.#optionElement('colour', colour)) this.selected.colour = colour;
    if (finish && this.#optionElement('finish', finish)) this.selected.finish = finish;
    if (fabric && this.#optionElement('fabric', fabric)) this.selected.fabric = fabric;
    if (fringe && (fringe === 'none' || this.#optionElement('fringe', fringe))) this.selected.fringe = fringe;
  }

  #readUrl() {
    const raw = new URLSearchParams(window.location.search).get('config');
    if (!raw) return;
    this.#applyConfig(raw);
  }

  #writeUrl() {
    const s = this.selected;
    const url = new URL(window.location.href);
    url.searchParams.set('config', this.#configString());
    window.history.replaceState(window.history.state, '', url);
    const urlField = this.querySelector('[ref="urlField"]');
    if (urlField instanceof HTMLInputElement) urlField.value = url.href;
  }
}

if (!customElements.get('albertina-configurator')) {
  customElements.define('albertina-configurator', AlbertinaConfigurator);
}
